// Test templates for Blood and Urine analysis
// Each test includes: name, category, unit, normalRange

export interface TestTemplate {
  id: string; // Added
  name: string;
  category: string;
  unit: string;
  normalRange: string;
  value: string;
  price?: number; // Added for billing
  type?: 'text' | 'number' | 'select';
  options?: string[];
}

export const defaultBloodTests: TestTemplate[] = [
  // Complete Blood Count (CBC)
  { id: "blood-1", name: "Hemoglobin", category: "Complete Blood Count - CBC", unit: "g/dL", normalRange: "12.0-15.5", value: "" },
  { id: "blood-2", name: "RBC Count", category: "Complete Blood Count - CBC", unit: "millions/µL", normalRange: "4.5-5.5", value: "" },
  { id: "blood-3", name: "WBC Count", category: "Complete Blood Count - CBC", unit: "thousands/µL", normalRange: "4.0-11.0", value: "" },
  { id: "blood-4", name: "Platelet Count", category: "Complete Blood Count - CBC", unit: "thousands/µL", normalRange: "150-450", value: "" },
  { id: "blood-5", name: "Hematocrit", category: "Complete Blood Count - CBC", unit: "%", normalRange: "36-46", value: "" },
  { id: "blood-6", name: "MCV", category: "Complete Blood Count - CBC", unit: "fL", normalRange: "80-100", value: "" },
  { id: "blood-7", name: "MCH", category: "Complete Blood Count - CBC", unit: "pg", normalRange: "27-32", value: "" },
  { id: "blood-8", name: "MCHC", category: "Complete Blood Count - CBC", unit: "g/dL", normalRange: "32-36", value: "" },
  { id: "blood-9", name: "RDW", category: "Complete Blood Count - CBC", unit: "%", normalRange: "11.5-14.5", value: "" },
  { id: "blood-10", name: "MPV", category: "Complete Blood Count - CBC", unit: "fL", normalRange: "7.2-11.1", value: "" },

  // Differential Count
  { id: "blood-11", name: "Neutrophils", category: "Differential Count", unit: "%", normalRange: "50-70", value: "" },
  { id: "blood-12", name: "Lymphocytes", category: "Differential Count", unit: "%", normalRange: "20-40", value: "" },
  { id: "blood-13", name: "Monocytes", category: "Differential Count", unit: "%", normalRange: "2-8", value: "" },
  { id: "blood-14", name: "Eosinophils", category: "Differential Count", unit: "%", normalRange: "1-4", value: "" },
  { id: "blood-15", name: "Basophils", category: "Differential Count", unit: "%", normalRange: "0-2", value: "" },

  // Blood Sugar
  { id: "blood-16", name: "Fasting Blood Sugar", category: "Blood Sugar", unit: "mg/dL", normalRange: "70-100", value: "" },
  { id: "blood-17", name: "Random Blood Sugar", category: "Blood Sugar", unit: "mg/dL", normalRange: "70-140", value: "" },
  { id: "blood-18", name: "Post Prandial Blood Sugar", category: "Blood Sugar", unit: "mg/dL", normalRange: "<140", value: "" },
  { id: "blood-19", name: "HbA1c", category: "Blood Sugar", unit: "%", normalRange: "4.0-6.0", value: "" },

  // Lipid Profile
  { id: "blood-20", name: "Total Cholesterol", category: "Lipid Profile", unit: "mg/dL", normalRange: "<200", value: "" },
  { id: "blood-21", name: "HDL Cholesterol", category: "Lipid Profile", unit: "mg/dL", normalRange: ">40", value: "" },
  { id: "blood-22", name: "LDL Cholesterol", category: "Lipid Profile", unit: "mg/dL", normalRange: "<100", value: "" },
  { id: "blood-23", name: "VLDL Cholesterol", category: "Lipid Profile", unit: "mg/dL", normalRange: "<30", value: "" },
  { id: "blood-24", name: "Triglycerides", category: "Lipid Profile", unit: "mg/dL", normalRange: "<150", value: "" },
  { id: "blood-25", name: "Total/HDL Ratio", category: "Lipid Profile", unit: "ratio", normalRange: "<4.5", value: "" },
  { id: "blood-26", name: "LDL/HDL Ratio", category: "Lipid Profile", unit: "ratio", normalRange: "<2.5", value: "" },

  // Liver Function Tests
  { id: "blood-27", name: "Total Bilirubin", category: "Liver Function Tests - LFT", unit: "mg/dL", normalRange: "0.3-1.2", value: "" },
  { id: "blood-28", name: "Direct Bilirubin", category: "Liver Function Tests - LFT", unit: "mg/dL", normalRange: "0.0-0.3", value: "" },
  { id: "blood-29", name: "Indirect Bilirubin", category: "Liver Function Tests - LFT", unit: "mg/dL", normalRange: "0.2-0.8", value: "" },
  { id: "blood-30", name: "SGPT/ALT", category: "Liver Function Tests - LFT", unit: "U/L", normalRange: "7-56", value: "" },
  { id: "blood-31", name: "SGOT/AST", category: "Liver Function Tests - LFT", unit: "U/L", normalRange: "10-40", value: "" },
  { id: "blood-32", name: "Alkaline Phosphatase", category: "Liver Function Tests - LFT", unit: "U/L", normalRange: "44-147", value: "" },
  { id: "blood-33", name: "Total Protein", category: "Liver Function Tests - LFT", unit: "g/dL", normalRange: "6.3-8.2", value: "" },
  { id: "blood-34", name: "Albumin", category: "Liver Function Tests - LFT", unit: "g/dL", normalRange: "3.5-5.0", value: "" },
  { id: "blood-35", name: "Globulin", category: "Liver Function Tests - LFT", unit: "g/dL", normalRange: "2.3-3.4", value: "" },
  { id: "blood-36", name: "A/G Ratio", category: "Liver Function Tests - LFT", unit: "ratio", normalRange: "1.1-2.5", value: "" },

  // Kidney Function Tests
  { id: "blood-37", name: "Blood Urea", category: "Kidney Function Tests - KFT", unit: "mg/dL", normalRange: "15-40", value: "" },
  { id: "blood-38", name: "Serum Creatinine", category: "Kidney Function Tests - KFT", unit: "mg/dL", normalRange: "0.6-1.4", value: "" },
  { id: "blood-39", name: "Blood Urea Nitrogen", category: "Kidney Function Tests - KFT", unit: "mg/dL", normalRange: "7-18", value: "" },
  { id: "blood-40", name: "Uric Acid", category: "Kidney Function Tests - KFT", unit: "mg/dL", normalRange: "3.4-7.0", value: "" },
  { id: "blood-41", name: "Sodium", category: "Kidney Function Tests - KFT", unit: "mEq/L", normalRange: "136-145", value: "" },
  { id: "blood-42", name: "Potassium", category: "Kidney Function Tests - KFT", unit: "mEq/L", normalRange: "3.5-5.1", value: "" },
  { id: "blood-43", name: "Chloride", category: "Kidney Function Tests - KFT", unit: "mEq/L", normalRange: "98-107", value: "" },

  // Thyroid Function Tests
  { id: "blood-44", name: "TSH", category: "Thyroid Function Tests - TFT", unit: "µIU/mL", normalRange: "0.27-4.2", value: "" },
  { id: "blood-45", name: "T3 Total", category: "Thyroid Function Tests - TFT", unit: "ng/dL", normalRange: "80-200", value: "" },
  { id: "blood-46", name: "T4 Total", category: "Thyroid Function Tests - TFT", unit: "µg/dL", normalRange: "5.1-14.1", value: "" },
  { id: "blood-47", name: "Free T3", category: "Thyroid Function Tests - TFT", unit: "pg/mL", normalRange: "2.0-4.4", value: "" },
  { id: "blood-48", name: "Free T4", category: "Thyroid Function Tests - TFT", unit: "ng/dL", normalRange: "0.93-1.7", value: "" },

  // Cardiac Markers
  { id: "blood-49", name: "Troponin I", category: "Cardiac Markers", unit: "ng/mL", normalRange: "<0.04", value: "" },
  { id: "blood-50", name: "Troponin T", category: "Cardiac Markers", unit: "ng/mL", normalRange: "<0.01", value: "" },
  { id: "blood-51", name: "CK-MB", category: "Cardiac Markers", unit: "ng/mL", normalRange: "0.0-6.3", value: "" },
  { id: "blood-52", name: "LDH", category: "Cardiac Markers", unit: "U/L", normalRange: "140-280", value: "" },

  // Inflammatory Markers
  { id: "blood-53", name: "ESR", category: "Inflammatory Markers", unit: "mm/hr", normalRange: "0-30", value: "" },
  { id: "blood-54", name: "CRP", category: "Inflammatory Markers", unit: "mg/L", normalRange: "<3.0", value: "" },
  { id: "blood-55", name: "RA Factor", category: "Inflammatory Markers", unit: "IU/mL", normalRange: "<20", value: "" },

  // Vitamins & Minerals
  { id: "blood-56", name: "Vitamin D", category: "Vitamins & Minerals", unit: "ng/mL", normalRange: "30-100", value: "" },
  { id: "blood-57", name: "Vitamin B12", category: "Vitamins & Minerals", unit: "pg/mL", normalRange: "200-900", value: "" },
  { id: "blood-58", name: "Folate", category: "Vitamins & Minerals", unit: "ng/mL", normalRange: "2.7-17.0", value: "" },
  { id: "blood-59", name: "Iron", category: "Vitamins & Minerals", unit: "µg/dL", normalRange: "60-170", value: "" },
  { id: "blood-60", name: "TIBC", category: "Vitamins & Minerals", unit: "µg/dL", normalRange: "240-450", value: "" },
  { id: "blood-61", name: "Transferrin Saturation", category: "Vitamins & Minerals", unit: "%", normalRange: "20-50", value: "" },
  { id: "blood-62", name: "Ferritin", category: "Vitamins & Minerals", unit: "ng/mL", normalRange: "15-150", value: "" },

  // Hormones
  { id: "blood-63", name: "Insulin", category: "Hormones", unit: "µIU/mL", normalRange: "2.6-24.9", value: "" },
  { id: "blood-64", name: "Cortisol", category: "Hormones", unit: "µg/dL", normalRange: "6.2-19.4", value: "" },
  { id: "blood-65", name: "Testosterone", category: "Hormones", unit: "ng/dL", normalRange: "300-1000", value: "" },
  { id: "blood-66", name: "Prolactin", category: "Hormones", unit: "ng/mL", normalRange: "4.8-23.3", value: "" },

  // Serology - NEW CATEGORY
  { id: "blood-67", name: "Widal Test - Salmonella Typhi O", category: "Serology", unit: "dilution", normalRange: "<1:80", value: "" },
  { id: "blood-68", name: "Widal Test - Salmonella Typhi H", category: "Serology", unit: "dilution", normalRange: "<1:80", value: "" },
  { id: "blood-69", name: "Widal Test - Salmonella Paratyphi A H", category: "Serology", unit: "dilution", normalRange: "<1:80", value: "" },
  { id: "blood-70", name: "Widal Test - Salmonella Paratyphi B H", category: "Serology", unit: "dilution", normalRange: "<1:80", value: "" },
  { id: "blood-71", name: "HBsAg", category: "Serology", unit: "result", normalRange: "Non-Reactive", value: "" },
  { id: "blood-72", name: "Anti-HCV", category: "Serology", unit: "result", normalRange: "Non-Reactive", value: "" },
  { id: "blood-73", name: "HIV 1&2", category: "Serology", unit: "result", normalRange: "Non-Reactive", value: "" },
  { id: "blood-74", name: "VDRL", category: "Serology", unit: "result", normalRange: "Non-Reactive", value: "" },

  // ABO Blood Grouping - NEW CATEGORY
  { id: "blood-75", name: "ABO Blood Group", category: "ABO Blood Grouping", unit: "group", normalRange: "A/B/AB/O", value: "" },
  { id: "blood-76", name: "Rh Factor", category: "ABO Blood Grouping", unit: "type", normalRange: "Positive/Negative", value: "" },
  { id: "blood-77", name: "Anti-A", category: "ABO Blood Grouping", unit: "reaction", normalRange: "As per blood group", value: "" },
  { id: "blood-78", name: "Anti-B", category: "ABO Blood Grouping", unit: "reaction", normalRange: "As per blood group", value: "" },
  { id: "blood-79", name: "Anti-D", category: "ABO Blood Grouping", unit: "reaction", normalRange: "As per Rh type", value: "" },
];

export const defaultUrineTests: TestTemplate[] = [
  // Physical Examination
  { id: "urine-1", name: "Color", category: "Physical Examination", unit: "", normalRange: "Pale Yellow", value: "" },
  { id: "urine-2", name: "Appearance", category: "Physical Examination", unit: "", normalRange: "Clear", value: "" },
  { id: "urine-3", name: "Specific Gravity", category: "Physical Examination", unit: "", normalRange: "1.003-1.030", value: "" },
  { id: "urine-4", name: "pH", category: "Physical Examination", unit: "", normalRange: "5.0-8.0", value: "" },

  // Chemical Examination
  { id: "urine-5", name: "Protein", category: "Chemical Examination", unit: "", normalRange: "Nil/Trace", value: "" },
  { id: "urine-6", name: "Glucose", category: "Chemical Examination", unit: "", normalRange: "Nil", value: "" },
  { id: "urine-7", name: "Ketones", category: "Chemical Examination", unit: "", normalRange: "Nil", value: "" },
  { id: "urine-8", name: "Blood", category: "Chemical Examination", unit: "", normalRange: "Nil", value: "" },
  { id: "urine-9", name: "Bilirubin", category: "Chemical Examination", unit: "", normalRange: "Nil", value: "" },
  { id: "urine-10", name: "Urobilinogen", category: "Chemical Examination", unit: "", normalRange: "Normal", value: "" },
  { id: "urine-11", name: "Nitrites", category: "Chemical Examination", unit: "", normalRange: "Negative", value: "" },
  { id: "urine-12", name: "Leukocyte Esterase", category: "Chemical Examination", unit: "", normalRange: "Negative", value: "" },

  // Microscopic Examination
  { id: "urine-13", name: "RBC", category: "Microscopic Examination", unit: "/hpf", normalRange: "0-2", value: "" },
  { id: "urine-14", name: "WBC", category: "Microscopic Examination", unit: "/hpf", normalRange: "0-5", value: "" },
  { id: "urine-15", name: "Epithelial Cells", category: "Microscopic Examination", unit: "/hpf", normalRange: "Few", value: "" },
  { id: "urine-16", name: "Bacteria", category: "Microscopic Examination", unit: "", normalRange: "Few/Nil", value: "" },
  { id: "urine-17", name: "Crystals", category: "Microscopic Examination", unit: "", normalRange: "Nil", value: "" },
  { id: "urine-18", name: "Casts", category: "Microscopic Examination", unit: "", normalRange: "Nil", value: "" },
  { id: "urine-19", name: "Mucus", category: "Microscopic Examination", unit: "", normalRange: "Nil/Few", value: "" },
  { id: "urine-20", name: "Yeast", category: "Microscopic Examination", unit: "", normalRange: "Nil", value: "" },

  // Urine Culture
  { id: "urine-21", name: "Bacterial Count", category: "Urine Culture", unit: "CFU/mL", normalRange: "<10,000", value: "" },
  { id: "urine-22", name: "Organism Identified", category: "Urine Culture", unit: "", normalRange: "No Growth", value: "" },
  { id: "urine-23", name: "Antibiotic Sensitivity", category: "Urine Culture", unit: "", normalRange: "As per organism", value: "" },
];

export const defaultStoolTests: TestTemplate[] = [
  // Physical Examination
  { id: "stool-1",  name: "Color",        category: "Physical Examination", unit: "", normalRange: "Brown",           value: "" },
  { id: "stool-2",  name: "Consistency",  category: "Physical Examination", unit: "", normalRange: "Formed",          value: "" },
  { id: "stool-3",  name: "Odour",        category: "Physical Examination", unit: "", normalRange: "Characteristic",  value: "" },
  { id: "stool-4",  name: "Reaction (pH)",category: "Physical Examination", unit: "", normalRange: "6.5-7.5",         value: "" },
  { id: "stool-5",  name: "Mucus",        category: "Physical Examination", unit: "", normalRange: "Absent",          value: "" },
  { id: "stool-6",  name: "Blood",        category: "Physical Examination", unit: "", normalRange: "Absent",          value: "" },

  // Chemical Examination
  { id: "stool-7",  name: "Occult Blood (FOB)", category: "Chemical Examination", unit: "", normalRange: "Negative",  value: "" },
  { id: "stool-8",  name: "Reducing Substances",category: "Chemical Examination", unit: "", normalRange: "Absent",    value: "" },
  { id: "stool-9",  name: "Fat (Sudan III stain)",category: "Chemical Examination",unit: "", normalRange: "Absent",   value: "" },
  { id: "stool-10", name: "Bile Salts",         category: "Chemical Examination", unit: "", normalRange: "Present",   value: "" },
  { id: "stool-11", name: "Bile Pigments",      category: "Chemical Examination", unit: "", normalRange: "Present",   value: "" },

  // Microscopic Examination
  { id: "stool-12", name: "RBC",                       category: "Microscopic Examination", unit: "/hpf",  normalRange: "Nil",         value: "" },
  { id: "stool-13", name: "Pus Cells / WBC",           category: "Microscopic Examination", unit: "/hpf",  normalRange: "0-4",         value: "" },
  { id: "stool-14", name: "Epithelial Cells",           category: "Microscopic Examination", unit: "/hpf",  normalRange: "Few",         value: "" },
  { id: "stool-15", name: "Muscle Fibres",              category: "Microscopic Examination", unit: "/hpf",  normalRange: "Absent",      value: "" },
  { id: "stool-16", name: "Vegetable Cells",            category: "Microscopic Examination", unit: "/hpf",  normalRange: "Few",         value: "" },
  { id: "stool-17", name: "Yeast / Fungal Elements",    category: "Microscopic Examination", unit: "",      normalRange: "Absent",      value: "" },
  { id: "stool-18", name: "Fat Globules",               category: "Microscopic Examination", unit: "/hpf",  normalRange: "Absent",      value: "" },
  { id: "stool-19", name: "Starch Granules",            category: "Microscopic Examination", unit: "/hpf",  normalRange: "Absent",      value: "" },

  // Parasitology
  { id: "stool-20", name: "Ova of Parasites",          category: "Parasitology", unit: "", normalRange: "Not Seen",  value: "" },
  { id: "stool-21", name: "Cysts of Parasites",        category: "Parasitology", unit: "", normalRange: "Not Seen",  value: "" },
  { id: "stool-22", name: "Trophozoites",              category: "Parasitology", unit: "", normalRange: "Not Seen",  value: "" },
  { id: "stool-23", name: "Adult Worms / Segments",    category: "Parasitology", unit: "", normalRange: "Not Seen",  value: "" },

  // Bacteriology (Culture & Sensitivity)
  { id: "stool-24", name: "Organism Isolated",         category: "Bacteriology", unit: "", normalRange: "No Growth",  value: "" },
  { id: "stool-25", name: "Colony Count",              category: "Bacteriology", unit: "CFU/mL", normalRange: "< 10,000", value: "" },
  { id: "stool-26", name: "Antibiotic Sensitivity",    category: "Bacteriology", unit: "", normalRange: "As per organism", value: "" },

  // Rotavirus / Adenovirus Antigen (Rapid Test)
  { id: "stool-27", name: "Rotavirus Antigen",         category: "Rapid Antigen Tests", unit: "", normalRange: "Negative", value: "" },
  { id: "stool-28", name: "Adenovirus Antigen",        category: "Rapid Antigen Tests", unit: "", normalRange: "Negative", value: "" },
  { id: "stool-29", name: "H. pylori Antigen",         category: "Rapid Antigen Tests", unit: "", normalRange: "Negative", value: "" },
];

// Template for custom test entry
export const customTestTemplate: Partial<TestTemplate> = {
  name: "",
  category: "Custom Tests",
  unit: "",
  normalRange: "",
  value: ""
};

// Function to create a new custom test
export const createCustomTest = (testData: Partial<TestTemplate> = {}): TestTemplate & { id: string; isCustom: boolean } => ({
  ...customTestTemplate,
  ...testData,
  id: Date.now().toString(), // Unique identifier for custom tests
  isCustom: true
} as TestTemplate & { id: string; isCustom: boolean });

// Function to get all available categories
export const getBloodTestCategories = (): string[] => {
  const categories = [...new Set(defaultBloodTests.map(test => test.category))];
  return [...categories, "Custom Tests"];
};

export const getUrineTestCategories = (): string[] => {
  const categories = [...new Set(defaultUrineTests.map(test => test.category))];
  return [...categories, "Custom Tests"];
};

export const getStoolTestCategories = (): string[] => {
  const categories = [...new Set(defaultStoolTests.map(test => test.category))];
  return [...categories, "Custom Tests"];
};


// Helper function to filter tests with values for PDF generation
export const getTestsWithValues = (tests: TestTemplate[]): TestTemplate[] => {
  return tests.filter(test => test.value && test.value.trim() !== "");
};

// Helper function to group tests by category (only those with values)
export const getGroupedTestsWithValues = (tests: TestTemplate[]): Record<string, TestTemplate[]> => {
  const testsWithValues = getTestsWithValues(tests);
  return testsWithValues.reduce((groups, test) => {
    const category = test.category || 'Other Tests';
    if (!groups[category]) groups[category] = [];
    groups[category].push(test);
    return groups;
  }, {} as Record<string, TestTemplate[]>);
};